import rawDefaults from './tour.config.json';

/**
 * Tunable timings and body selection for tour mode, in one place so the pacing
 * can be retuned without touching the state machine in `Game.ts`.
 *
 * `transitDurationSec` is deliberately a fixed cinematic duration rather than
 * distance divided by a travel speed: interplanetary separations span orders
 * of magnitude, and a tour that took its travel time from real distance would
 * either crawl between neighbouring moons or spend real minutes crossing to
 * the next planet. Every leg costs the same, short amount of time regardless
 * of how far apart the two bodies actually are.
 */
export interface TourConfig {
  /** Cinematic flight time between two tour stops, seconds. */
  transitDurationSec: number;
  /** Hard ceiling `transitDurationSec` is clamped to, seconds. */
  maxTransitDurationSec: number;
  /** How long the camera circles a body before descending, seconds. */
  orbitDurationSec: number;
  /** Number of full revolutions completed during the orbit phase. */
  orbitRevolutions: number;
  /** Orbit standoff distance, in multiples of the body's radius. */
  orbitStandoffRadii: number;
  /** Tilt of the orbit plane off the body's equator, degrees. */
  orbitInclinationDeg: number;
  /** Duration of the spiral descent from orbit to the surface pass, seconds. */
  descentDurationSec: number;
  /** Altitude of the low surface pass above the terrain datum, metres. */
  surfaceFlybyAltitudeM: number;
  /** Duration of the low surface pass, seconds. */
  surfaceFlybyDurationSec: number;
  /**
   * Ground speed of the surface pass, metres per second.
   *
   * The pass is authored as a speed rather than as an angular sweep because
   * how fast it *reads* depends on metres of ground crossed per second at a
   * fixed altitude, and nothing else — an angle that looks sedate over a gas
   * giant is a hypersonic blur over a small moon. A fixed 14-degree sweep,
   * which is what this replaced, worked out to roughly 62 km/s over Halcyon:
   * the terrain never resolved into anything the eye could follow.
   */
  surfaceFlybySpeedMps: number;
  /**
   * Hard cap on the angular sweep the speed above is allowed to produce,
   * degrees. Only binds on a body small enough that the configured speed would
   * carry the camera an appreciable fraction of the way around it.
   */
  surfaceFlybySweepDeg: number;
  /** Whether each planet's moons are toured immediately after the planet. */
  includeMoons: boolean;
  /** Body ids to skip entirely. */
  excludeBodyIds: string[];
  /** Explicit planet visiting order, by id. Empty means natural system order. */
  bodyOrder: string[];
}

export const DEFAULT_TOUR_CONFIG: TourConfig = rawDefaults as TourConfig;

/** Merges partial overrides onto the defaults and enforces the transit cap. */
export function resolveTourConfig(overrides?: Partial<TourConfig>): TourConfig {
  const merged = { ...DEFAULT_TOUR_CONFIG, ...overrides };
  merged.transitDurationSec = Math.min(merged.transitDurationSec, merged.maxTransitDurationSec);
  return merged;
}

/**
 * Angular sweep of one body's surface pass, radians — `surfaceFlybySpeedMps`
 * held for `surfaceFlybyDurationSec` at `flightRadius`, capped by
 * `surfaceFlybySweepDeg`.
 *
 * Every consumer of the pass geometry (the terrain-clearance scan, the
 * orientation preview during the descent, the pass itself) goes through this
 * one function. They used to each read `surfaceFlybySweepDeg` directly, which
 * was harmless only while the sweep was a constant.
 */
export function tourFlybySweepRad(cfg: TourConfig, flightRadius: number): number {
  const cap = (cfg.surfaceFlybySweepDeg * Math.PI) / 180;
  const fromSpeed = (cfg.surfaceFlybySpeedMps * cfg.surfaceFlybyDurationSec) / Math.max(1, flightRadius);
  return Math.min(cap, fromSpeed);
}
